import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Context, ErrorEntity, Error as ErrorI } from "../../../components/root/context";
import { parseEntity } from "../../../lib/utils/entityParser";
import DangerIcon from "../icons/danger-icon";
import LockIcon from "../icons/lock-icon";
import { Button } from "./button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./dialog";

const ErrorModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { error, setError } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const [rerenderInvite, setRerenderInvite] = useState<boolean>();
  const { logout } = useAuth0();

  const onClose = (withoutReload?: boolean) => {
    setIsOpen(false);
    if (setError) {
      setError(undefined);
    }
    if (withoutReload === undefined || withoutReload) {
      return;
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (error) {
      if (error.location.redirect) {
        setRerenderInvite(true);
        navigate(error.location.path + (error.mail ? `?mail=${error.mail}` : ""));
        setIsOpen(false);
        setError && setError(undefined);
      } else {
        setIsOpen(true);
        setRerenderInvite(true);
      }
    }
  }, [error, navigate, setError]);

  useEffect(() => {
    if (!error && isOpen) {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (rerenderInvite) {
      setRerenderInvite(undefined);
    }
  }, [rerenderInvite]);

  const logOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <>
      {error &&
        error.entity !== ErrorEntity.InvalidIbanForbidden &&
        error.entity !== ErrorEntity.PaymentOverdueForbidden &&
        error.entity !== ErrorEntity.InvalidBicForbidden &&
        error.entity !== ErrorEntity.InvalidCodeBadRequest &&
        error.entity !== ErrorEntity.InvalidIbanAndBicForbidden &&
        error.entity !== ErrorEntity.OfferMailAlreadyInUseConflict &&
        error.entity !== ErrorEntity.OfferCompanyNameAlreadyInUseConflict &&
        error.entity !== ErrorEntity.OfferLinkInvalidNotFound &&
        error.entity !== ErrorEntity.OfferAlreadyAcceptedConflict &&
        error.entity !== ErrorEntity.OfferCheckoutTooManyRequests && (
          <Dialog
            open={isOpen}
            onOpenChange={(value) => {
              setIsOpen(value);
              if (value === false) {
                onClose(!error.withReload);
              }
            }}
          >
            <DialogContent
              onInteractOutside={(e) => {
                if (error.isClosable === false) {
                  e.preventDefault();
                  return;
                }
                onClose(!error.withReload);
              }}
              isClosable={error.isClosable}
            >
              <DialogHeader>
                <DialogTitle>{error.title}</DialogTitle>
              </DialogHeader>
              <div>
                <p>{error.message}</p>
              </div>
              <DialogFooter>
                <div className={`flex gap-3 w-full`}>
                  {error.isClosable && (
                    <>
                      <Button
                        className={error.location ? "grow" : ""}
                        variant="outline"
                        onClick={() => onClose(!error.withReload)}
                      >
                        Schließen
                      </Button>
                    </>
                  )}
                  {error.location.path && (
                    <>
                      {error.location.path === "/login" ? (
                        <Button className="grow" onClick={() => logOut()}>{`Ausloggen`}</Button>
                      ) : (
                        <Link to={error.location.path} className="grow flex">
                          <Button className="grow" onClick={() => onClose(true)}>{`${parseEntity(
                            error.entity
                          )}`}</Button>
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      {error && error.entity === ErrorEntity.PaymentOverdueForbidden && (
        <PaymentOverdueModal isOpen={isOpen} setIsOpen={setIsOpen} onClose={onClose} error={error} />
      )}
    </>
  );
};

const PaymentOverdueModal = ({
  isOpen,
  setIsOpen,
  onClose,
  error,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onClose: (value: boolean) => void;
  error: ErrorI;
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        setIsOpen(value);
        if (value === false) {
          onClose(!error.withReload);
        }
      }}
    >
      <DialogContent
        className="border-[#CE0909] border-4 flex flex-col gap-6"
        isClosable={false}
        onInteractOutside={(e) => {
          e.preventDefault();
          return;
        }}
      >
        <DialogHeader className="flex flex-col items-center gap-4">
          <div className="h-24">
            <LockIcon />
          </div>
          <DialogTitle className="text-2xl font-bold">Dein Konto ist gesperrt</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="text-[#CE0909] flex gap-2 items-center justify-center font-bold">
            <div className="h-4">
              <DangerIcon />
            </div>
            <p>Die aktuelle Rechnung ist überfällig</p>
          </div>
          <div className="text-center flex flex-col gap-4">
            <p className="text-[#888888] font-semibold">
              Gleiche die ausstehende Rechnung aus, um wieder Zugriff auf Dein Konto zu erhalten. Nach §4 Abs. 6 der AGB
              dürfen die bisher erstellten Designs erst nach Zahlungseingang verwendet und veröffentlicht werden.
            </p>
            <p className="text-[#ACACAC] text-sm">
              Handelt es sich um einen Fehler? Kontaktiere bitte buchhaltung@7i7.de
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ErrorModal;
